import { EDecision, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate, formatDateTime } from '@if/core/utils'
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Divider, IconButton, Link, Paper, Stack, Typography } from '@mui/material'
import {
  MechanicalWarrantyWorksheet,
  MechanicalWarrantyWorksheetDto,
} from '@src/containers/EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/mechanicalWarrantyWorksheetSchema'
import { useCreateMechanicalWarrantyWorksheet } from '@src/data/api/mechanical-warranty-worksheet/mechanical-warranty-worksheet-api'
import { useMerchantPaymentPlan } from '@src/data/selectors/credit-selectors'
import { Constants, CreditApplication, ETermMax } from '@src/data/types'
import { reportErrorToConsole } from '@src/services/error-logger'
import { getMinMaxPaymentDates } from '@src/services/paymentDate-utils'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  worksheet: MechanicalWarrantyWorksheet | null
  creditApplication: CreditApplication
  onWorksheetHistoryClicked: () => void
  editDisabled: boolean
}

const MechanicalWarrantyWorksheetInformationSection = ({
  worksheet,
  creditApplication,
  onWorksheetHistoryClicked,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createWorksheet] = useCreateMechanicalWarrantyWorksheet()

  const merchantPaymentPlan = useMerchantPaymentPlan(creditApplication)
  const paymentFrequency = creditApplication.isSinglePayment ? EPaymentFrequency.Single : EPaymentFrequency.Monthly
  const deliveryDate = formatDate(new Date())
  const [minFirstPaymentOn] = getMinMaxPaymentDates(deliveryDate, paymentFrequency)
  const editWorksheetDisabled = creditApplication.finalCreditDecision.decision !== EDecision.Approved || editDisabled
  const defaultProviderId = 'monolith*1'
  const defaultProductId = 'standard12'

  const handleCreateWorksheet = useCallback(async () => {
    const route = `/Applications/mechwarranty/${creditApplication.id}/worksheet/edit`
    if (worksheet === null) {
      const term = merchantPaymentPlan ? merchantPaymentPlan.loanTerm : ETermMax.twelve
      const newWorksheet: MechanicalWarrantyWorksheetDto = {
        id: '',
        versionTag: '',
        creditApplicationId: creditApplication.id,
        amountRequested: Constants.MinimumProductsLoanAmount,
        deliveryOn: deliveryDate,
        firstPaymentOn: formatDate(minFirstPaymentOn),
        paymentFrequency,
        term,
        isHighEstimate: false,
        paymentPlanId: merchantPaymentPlan ? merchantPaymentPlan.id : EPaymentPlan.RegularDailyInterests,
        status: EWorksheetStatus.Draft,
        providerId: defaultProviderId,
        policy: '',
        additions: { options: [], surcharges: [] },
        mechanicalWarrantyProductId: defaultProductId,
      }

      try {
        await createWorksheet(newWorksheet)
        navigate(route)
      } catch (error) {
        reportErrorToConsole(error)
      }
    } else {
      navigate(route)
    }
  }, [worksheet, creditApplication, merchantPaymentPlan, deliveryDate, minFirstPaymentOn, paymentFrequency, createWorksheet, navigate])
  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2} marginBottom={0.5}>
            <ButtonGroup variant="outlined" size="large">
              <Button
                id="btnEditWorksheet"
                component={Link}
                variant="outlined"
                href={`/Applications/mechwarranty/${creditApplication.id}/worksheet`}
                disabled={!worksheet}
              >
                {t('worksheet.financing')}
              </Button>
              <Button size="small" onClick={handleCreateWorksheet} disabled={editWorksheetDisabled}>
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            alignContent="center"
            divider={<Divider orientation="vertical" flexItem sx={{ height: 25, alignSelf: 'center' }} />}
          >
            <Typography variant="body1" component="div" gutterBottom>
              {t('common.LastModification')} : {worksheet && formatDateTime(worksheet?.updatedOn)}
            </Typography>

            <IconButton
              onClick={onWorksheetHistoryClicked}
              disabled={worksheet === null}
              sx={[
                {
                  fontSize: 'large',
                },
                worksheet === null
                  ? {
                      color: '',
                    }
                  : {
                      color: 'primary.main',
                    },
              ]}
            >
              <HistoryIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.decision')} :{' '}
              {worksheet?.finalDecision.decision &&
                t(`EWorksheetDecision.${worksheet.finalDecision.decision}`, { ns: 'enum' })}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheetCommon.amountRequested')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default MechanicalWarrantyWorksheetInformationSection
